import { useMemo, useState, type ReactNode } from "react";
import type { Contract, ContractListPagination } from "../../types/contract";
import { ContractListTable } from "./ContractListTable";
/**
 * Contract listing filters (FR-009–FR-011): icon-only date-range filter; no status filter.
 * Same UX pattern as TemplateLibraryView for Word add-in consistency.
 */
import { Funnel, RefreshCw, Search } from "lucide-react";
import {
  CONTRACT_DATE_RANGE_OPTIONS,
  type ContractDateRangeValue,
} from "../../constants/contractFilters";

type SelectContractViewProps = {
  contracts: Contract[];
  pagination: ContractListPagination;
  isLoading: boolean;
  /** True while a user-initiated list refresh is in flight. */
  isRefreshing: boolean;
  /** Optional banner message when routed here from document resolution. */
  resolveBanner?: { readonly tone: "info" | "error"; readonly message: string } | null;
  errorMessage: string | null;
  /** Error from a failed manual refresh; does not replace the last loaded list. */
  refreshError: string | null;
  onDismissRefreshError: () => void;
  /** Shown when opening a contract document in the host fails (distinct from list load error). */
  openDocumentError: string | null;
  openingContractId: string | null;
  searchTerm: string;
  selectedDateRangeDays?: ContractDateRangeValue;
  onContractTitleClick: (contract: Contract) => void;
  /** Opens the review route in the task pane (disabled in the list for completed contracts). */
  onGoToReview: (contract: Contract) => void;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (value: ContractDateRangeValue | undefined) => void;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onRetry: () => void;
};

export const SelectContractView = ({
  contracts,
  pagination,
  isLoading,
  isRefreshing,
  resolveBanner,
  errorMessage,
  refreshError,
  onDismissRefreshError,
  openDocumentError,
  openingContractId,
  searchTerm,
  selectedDateRangeDays,
  onContractTitleClick,
  onGoToReview,
  onSearchChange,
  onDateRangeChange,
  onPageChange,
  onRefresh,
  onRetry,
}: SelectContractViewProps) => {
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const activeDateFilterLabel = useMemo(
    () =>
      CONTRACT_DATE_RANGE_OPTIONS.find(
        (opt) => opt.value === selectedDateRangeDays,
      )?.label ?? null,
    [selectedDateRangeDays],
  );
  let content: ReactNode;
  if (errorMessage) {
    content = (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <p>{errorMessage}</p>
        <button
          type="button"
          className="mt-2 text-primary hover:underline"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  } else if (contracts.length === 0 && !isLoading) {
    content = (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Data not present
      </p>
    );
  } else {
    content = (
      <ContractListTable
        contracts={contracts}
        isLoading={isLoading}
        pagination={pagination}
        openingContractId={openingContractId}
        onContractTitleClick={onContractTitleClick}
        onGoToReview={onGoToReview}
        onPageChange={onPageChange}
      />
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold text-foreground">
          Select Contract
        </h2>
        <p className="text-sm text-muted-foreground">
          Choose a contract to review
        </p>
      </header>
      {resolveBanner ? (
        <div
          className={
            resolveBanner.tone === "error"
              ? "rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              : "rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          }
          role={resolveBanner.tone === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {resolveBanner.message}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-input-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="relative">
          <button
            type="button"
            aria-label="Filter contracts by date range"
            title={activeDateFilterLabel ?? "Filter contracts"}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border bg-input-background text-sm transition-all duration-200 ease-in-out ${
              activeDateFilterLabel
                ? "border-primary text-primary"
                : "border-gray-300 text-foreground"
            }`}
            onClick={() => setIsDateFilterOpen((prev) => !prev)}
          >
            <Funnel className="h-4 w-4" />
          </button>
          {isDateFilterOpen && (
            <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              {CONTRACT_DATE_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onClick={() => {
                    onDateRangeChange(option.value);
                    setIsDateFilterOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Refresh contract list"
          title="Refresh contract list"
          disabled={isRefreshing}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-input-background text-sm text-foreground transition-all duration-200 ease-in-out hover:bg-muted/40 disabled:pointer-events-none disabled:opacity-60"
          onClick={() => {
            onRefresh();
          }}
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            aria-hidden
          />
        </button>
      </div>
      {refreshError ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          role="alert"
        >
          <p>{refreshError}</p>
          <button
            type="button"
            className="mt-2 text-primary hover:underline"
            onClick={onDismissRefreshError}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {openDocumentError ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          role="alert"
        >
          {openDocumentError}
        </div>
      ) : null}
      {content}
    </section>
  );
};
